'use client';

import { createContext, type ReactNode, useContext } from 'react';
import type { SharedModalAdapter } from './types';

const ModalAdapterContext = createContext<SharedModalAdapter | null>(null);

export interface ModalAdapterProviderProps {
  adapter: SharedModalAdapter;
  children: ReactNode;
}

export function ModalAdapterProvider({ adapter, children }: ModalAdapterProviderProps) {
  return <ModalAdapterContext.Provider value={adapter}>{children}</ModalAdapterContext.Provider>;
}

/**
 * 코어(공유) 모달 어댑터 접근 훅. 컨텍스트에는 `SharedModalAdapter`만 저장된다.
 *
 * - 공유 코드(`pandatv-module`)는 이 훅을 인자 없이 호출해 코어 계약만 본다.
 * - 앱 코드는 이 훅을 직접 쓰지 말고, 각 앱의 `provider/useModalAdapter.ts`가 노출하는
 *   앱 전용 `useModalAdapter`(이 훅에 앱 어댑터 타입을 박은 래퍼)를 통해 쓴다. 제네릭 인자는
 *   앱당 한 곳에만 둔다.
 */
export function useSharedModalAdapter<T extends SharedModalAdapter = SharedModalAdapter>(): T {
  const adapter = useContext(ModalAdapterContext);
  if (!adapter) {
    throw new Error('useSharedModalAdapter must be used within a ModalAdapterProvider');
  }
  return adapter as T;
}

/**
 * Optional version - returns null if no provider
 * Useful for gradual migration
 */
export function useSharedModalAdapterOptional<T extends SharedModalAdapter = SharedModalAdapter>(): T | null {
  return useContext(ModalAdapterContext) as T | null;
}
